import { Fragment } from 'react';
import { cn } from '../../lib/cn';

/**
 * Renders a parsed sentence as clickable tokens. Content words are buttons
 * that hand off to the parent's word-lookup handler; punctuation renders
 * inline and attaches to its neighbor (no orphan space before periods, etc.).
 *
 * Props:
 *   analysis: { text, parse_data: { tokens: [{ i, text, lemma, pos, tag }] } }
 *   onWordClick: (token) => void
 *   highlightedTokenIndex: number — token index to visually emphasize
 */

// Punctuation that should be glued to the *previous* token (no space before).
const ATTACHES_TO_PREV = /^[.,;:!?)\]}»"'’”]/;
// Punctuation that should be glued to the *next* token (no space after).
const ATTACHES_TO_NEXT = /[(\[{«"'‘“]$/;

function isPunctuationToken(t) {
  return t.pos === 'PUNCT' || /^[\p{P}]+$/u.test(t.text);
}

export default function SentenceDisplay({ analysis, onWordClick, highlightedTokenIndex }) {
  const tokens = analysis?.parse_data?.tokens || [];

  if (tokens.length === 0) {
    return (
      <p className="text-xl leading-[1.7] text-gray-800 dark:text-gray-100">
        {analysis?.text || ''}
      </p>
    );
  }

  return (
    <p className="text-xl leading-[1.7] text-gray-800 dark:text-gray-100">
      {tokens.map((t, i) => {
        const prev = tokens[i - 1];
        const needsLeadingSpace =
          i > 0 &&
          !ATTACHES_TO_PREV.test(t.text) &&
          !(prev && ATTACHES_TO_NEXT.test(prev.text));

        const isPunct = isPunctuationToken(t);
        const isHighlighted = i === highlightedTokenIndex;

        const node = isPunct ? (
          <span className="text-gray-400 dark:text-gray-500">{t.text}</span>
        ) : (
          <button
            onClick={() => onWordClick?.(t)}
            className={cn(
              'rounded px-0.5 transition-colors',
              'hover:bg-amber-100 dark:hover:bg-amber-900/40',
              isHighlighted && 'bg-amber-100 dark:bg-amber-900/40'
            )}
          >
            {t.text}
          </button>
        );

        return (
          <Fragment key={t.i ?? i}>
            {needsLeadingSpace && ' '}
            {node}
          </Fragment>
        );
      })}
    </p>
  );
}
